import { css } from '@emotion/react';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import {
  ButtonComponent, ButtonSize,
} from '~/_shared/baseComponents/buttons';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import type { TopUnderbarItem } from '../item/topUnderbarItem.types';

const NPS_COMPACT_BREAKPOINT = 500;
const NPS_SCALE_MAX_WIDTH = 580;
const NPS_HEADER_COMPACT_BREAKPOINT = 460;

const rootStyle = css({
  alignItems: 'center',
  containerType: 'inline-size',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  justifyContent: 'space-between',
  width: '100%',
});

const labelStyle = (theme: Theme) => css({
  color: theme.textColors.link,
  fontSize: 16,
  fontWeight: 700,
});

const actionsWrapperStyle = css({
  alignItems: 'center',
  flexShrink: 0,
  marginLeft: 'auto',
  order: 3,
  [`@container (min-width: ${NPS_HEADER_COMPACT_BREAKPOINT}px)`]: {
    order: 1,
  },
});

const fadeStyle = (isVisible: boolean) => css({
  opacity: isVisible ? 1 : 0,
  transition: 'opacity 0.2s',
});

const savedIndicatorStyle = (isVisible: boolean) =>
  (theme: Theme) => css(fadeStyle(isVisible), {
    alignItems: 'center',
    color: theme.textColors.success,
    display: 'flex',
    fontSize: 16,
    gap: 4,
  });

const npsButtonsContainerStyle = css({
  containerType: 'inline-size',
  flexBasis: '100%',
  maxWidth: NPS_SCALE_MAX_WIDTH,
  order: 2,
  width: '100%',
});

const npsButtonsWrapperStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  [`@container (max-width: ${NPS_COMPACT_BREAKPOINT}px)`]: {
    justifyContent: 'center',
  },
});

const npsButtonStyle = (theme: Theme, isSelected: boolean) => {
  const borderColor = isSelected ? theme.borderColors.activeItem : theme.borderColors.primary;
  const getBoxShadow = (width: number) => `0 0 0 ${width}px ${borderColor} inset`;
  const getBackground = (color: string) => isSelected ? theme.backgroundColors.highlight : color;

  return css({
    alignItems: 'center',
    backgroundColor: getBackground(theme.backgroundColors.secondary),
    borderRadius: 4,
    boxShadow: getBoxShadow(1),
    color: theme.textColors.primary,
    display: 'flex',
    flexShrink: 0,
    height: 44,
    justifyContent: 'center',
    userSelect: 'none',
    width: 40,
    '&:hover': {
      backgroundColor: getBackground(theme.backgroundColors.tertiary),
      boxShadow: getBoxShadow(isSelected ? 2 : 1),
    },
  });
};

const npsLineBreakStyle = css({
  display: 'none',
  [`@container (max-width: ${NPS_COMPACT_BREAKPOINT}px)`]: {
    display: 'block',
    width: '100%',
  },
});

const npsLabelsRowStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  [`@container (max-width: ${NPS_COMPACT_BREAKPOINT}px)`]: {
    display: 'none',
  },
});

const npsSubLabelStyle = (theme: Theme) => css({
  fontSize: 12,
  color: theme.textColors.link,
});

const NPS_SCORES_ROW_1 = [0, 1, 2, 3, 4, 5];
const NPS_SCORES_ROW_2 = [6, 7, 8, 9, 10];

type TopUnderbarNpsProps = Readonly<{
  isScoreSaved: boolean;
  score: number | null;
  onScoreSelect: (score: number) => void;
  onTellUsMore: () => void;
}>;

export const TopUnderbarNpsComponent: FC<TopUnderbarNpsProps> = ({ isScoreSaved, score, onScoreSelect, onTellUsMore }) => {
  const [t] = useTranslation();
  const theme = useTheme();

  const renderNpsButtons = (scores: ReadonlyArray<number>) =>
    scores.map(i => (
      <div
        key={i}
        css={npsButtonStyle(theme, score === i)}
        onClick={() => onScoreSelect(i)}
      >
        {i}
      </div>
    ));

  const isScoreSelected = notNullsy(score);

  return (
    <div css={rootStyle}>
      <div css={labelStyle}>{t('nps.scale.label')}</div>

      <Inline
        css={actionsWrapperStyle}
        gap={20}
      >
        <div css={savedIndicatorStyle(isScoreSaved)}>
          <FontAwesomeIcon icon={faCheck} />
          {t('Saved')}
        </div>

        <div css={fadeStyle(isScoreSelected)}>
          <ButtonComponent
            isDisabled={!isScoreSelected}
            onClick={onTellUsMore}
            size={ButtonSize.Medium}
            text={t('nps.details.actionLabel')}
          />
        </div>
      </Inline>

      <Stack
        css={npsButtonsContainerStyle}
        gap={8}
      >
        <Inline
          css={npsButtonsWrapperStyle}
          gap={4}
        >
          {renderNpsButtons(NPS_SCORES_ROW_1)}
          <div css={npsLineBreakStyle} />
          {renderNpsButtons(NPS_SCORES_ROW_2)}
        </Inline>
        <div css={npsLabelsRowStyle}>
          <span css={npsSubLabelStyle(theme)}>{t('nps.scale.bad')}</span>
          <span css={npsSubLabelStyle(theme)}>{t('nps.scale.good')}</span>
        </div>
      </Stack>
    </div>
  );
};

export const renderNps = ({ isScoreSaved, score, onScoreSelect, onTellUsMore, onClose, theme }: {
  isScoreSaved: boolean;
  onClose?: () => void;
  onScoreSelect: (score: number) => void;
  onTellUsMore: () => void;
  score: number | null;
  theme: Theme;
}): TopUnderbarItem => ({
  key: 'nps',
  children: (
    <TopUnderbarNpsComponent
      isScoreSaved={isScoreSaved}
      onScoreSelect={onScoreSelect}
      onTellUsMore={onTellUsMore}
      score={score}
    />
  ),
  colors: {
    backgroundColor: theme.contrasts.selectedAccent2Background,
    color: theme.contrasts.selectedAccent2,
    borderColor: theme.borderColors.activeItem,
  },
  onClose,
  padding: '6px 12px',
});
